import type { ComicBubbleObject } from "../comic/comicTypes";
import type {
  AddComicObjectCommand,
  ClearPanelCommand,
  DeleteComicObjectCommand,
  DuplicatePanelCommand,
  EditComicTextCommand,
  HistoryCommand,
  TransformComicObjectCommand,
} from "./historyTypes";

let comicCommandSequence = 0;

function nextId(kind: string): string {
  comicCommandSequence += 1;
  return `${kind}-${comicCommandSequence}`;
}

export function immutableComicObject(object: ComicBubbleObject): Readonly<ComicBubbleObject> {
  return Object.freeze({ ...object, data: Object.freeze({ ...object.data }) });
}

export function createAddComicObjectCommand(object: ComicBubbleObject): AddComicObjectCommand {
  return Object.freeze({
    kind: "addComicObject" as const,
    id: nextId("add-object"),
    object: immutableComicObject(object),
  });
}

export function createTransformComicObjectCommand(
  before: ComicBubbleObject,
  after: ComicBubbleObject,
): TransformComicObjectCommand {
  return Object.freeze({
    kind: "transformComicObject" as const,
    id: nextId("transform-object"),
    before: immutableComicObject(before),
    after: immutableComicObject(after),
  });
}

export function createEditComicTextCommand(
  objectId: string,
  beforeText: string,
  afterText: string,
): EditComicTextCommand {
  return Object.freeze({
    kind: "editComicText" as const,
    id: nextId("edit-text"),
    objectId,
    beforeText,
    afterText,
  });
}

export function createDeleteComicObjectCommand(
  object: ComicBubbleObject,
): DeleteComicObjectCommand {
  return Object.freeze({
    kind: "deleteComicObject" as const,
    id: nextId("delete-object"),
    object: immutableComicObject(object),
  });
}

export function createClearPanelCommand(panelId: string): ClearPanelCommand {
  return Object.freeze({ kind: "clearPanel" as const, id: nextId("clear-panel"), panelId });
}

export function createDuplicatePanelCommand(
  sourcePanelId: string,
  targetPanelId: string,
  duplicatedObjects: ReadonlyArray<ComicBubbleObject>,
): DuplicatePanelCommand {
  return Object.freeze({
    kind: "duplicatePanel" as const,
    id: nextId("duplicate-panel"),
    sourcePanelId,
    targetPanelId,
    duplicatedObjects: Object.freeze(duplicatedObjects.map(immutableComicObject)),
  });
}

export function estimateHistoryCommandBytes(command: HistoryCommand): number {
  if (command.kind === "stroke") {
    const textBytes = (command.settings.colour.length + command.settings.tool.length) * 2;
    return 144 + textBytes + command.points.length * 24;
  }
  if (command.kind === "duplicatePanel") {
    return (
      160 + command.duplicatedObjects.reduce((total, object) => total + estimateObject(object), 0)
    );
  }
  if (command.kind === "addComicObject" || command.kind === "deleteComicObject") {
    return 128 + estimateObject(command.object);
  }
  if (command.kind === "transformComicObject") {
    return 160 + estimateObject(command.before) + estimateObject(command.after);
  }
  if (command.kind === "editComicText") {
    return 128 + (command.beforeText.length + command.afterText.length) * 2;
  }
  if (command.kind === "layerMutation" || command.kind === "mergeLayerDown") {
    return 160 + (command.before.layers.length + command.after.layers.length) * 180;
  }
  if (command.kind === "rasterSelection" || command.kind === "shape" || command.kind === "fill")
    return command.estimatedBytes;
  return 128;
}

function estimateObject(object: Readonly<ComicBubbleObject>): number {
  return (
    240 +
    (object.id.length +
      object.panelId.length +
      object.layerId.length +
      object.type.length +
      object.data.text.length +
      object.data.fillColor.length +
      object.data.strokeColor.length) *
      2
  );
}
